// TanStack Query hooks per backend endpoint. Type-safe through-and-through:
// every payload shape comes from @incorposano/api-client which itself comes
// from shared/api_contract.json. A backend wire change → frontend tsc fail.
//
// Naming: useFooQuery for GETs, useFooMutation for POSTs that mutate state.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { ApiError } from "@incorposano/api-client";

import type {
  ActiveSessionResponse,
  AuthTokenResponse,
  ChatRequest,
  ChatResponse,
  CreateSessionResponse,
  DeleteLastPairResponse,
  GenericOkResponse,
  HealthResponse,
  IngredientSummary,
  LoginRequest,
  MeResponse,
  PasswordResetConfirm,
  PasswordResetRequest,
  RecommendRequest,
  RecommendResponse,
  SessionSummary,
  SignupRequest,
  SignupResponse,
  SkuSummary,
  TenantBrandingResponse,
  UserProfileSummaryResponse,
} from "@incorposano/api-client";

import { api } from "@/api/client";
import { useAuth } from "@/stores/auth";
import { useSession } from "@/stores/session";

// ─── Query keys (typed factory) ──────────────────────────────

export const qk = {
  health: ["health"] as const,
  catalogIngredients: ["catalog", "ingredients"] as const,
  catalogSkus: ["catalog", "skus"] as const,
  recommend: (sessionId: string) => ["recommend", sessionId] as const,
  me: ["auth", "me"] as const,
  activeSession: ["sessions", "active"] as const,
  latestCompletedSession: ["sessions", "latest-completed"] as const,
  sessionsList: ["sessions", "list"] as const,
  profileSummary: ["me", "profile-summary"] as const,
  tenantBranding: ["tenant", "branding"] as const,
};

// ─── /health ─────────────────────────────────────────────────

export function useHealthQuery(): UseQueryResult<HealthResponse> {
  return useQuery({
    queryKey: qk.health,
    queryFn: () => api.health(),
    staleTime: 30_000,
  });
}

// ─── /tenant/branding ────────────────────────────────────────
//
// Effectively immutable for the lifetime of the SPA — the backend only
// changes branding on deploy. staleTime: Infinity ensures one fetch per
// page load; gcTime keeps it in cache so unmounting the only consumer
// doesn't trigger a refetch on the next mount.

export function useTenantBrandingQuery(): UseQueryResult<TenantBrandingResponse> {
  return useQuery({
    queryKey: qk.tenantBranding,
    queryFn: () => api.tenantBranding(),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
}

// ─── /sessions (POST, idempotent-ish) ────────────────────────

export function useCreateSessionMutation(): UseMutationResult<
  CreateSessionResponse,
  Error,
  void
> {
  const startSession = useSession((s) => s.startSession);
  return useMutation({
    mutationFn: () => api.createSession(),
    onSuccess: (data) => {
      startSession({
        sessionId: data.session_id,
        greeting: data.greeting_message,
      });
    },
  });
}

// ─── /sessions/active (resume) ──────────────────────────────

export function useActiveSessionQuery(
  enabled: boolean,
): UseQueryResult<ActiveSessionResponse | null> {
  return useQuery({
    queryKey: qk.activeSession,
    queryFn: () => api.getActiveSession(),
    enabled,
    staleTime: 0,
    retry: false,
  });
}

/**
 * ICSP-37: latest-completed-session fallback for /recommendation mount.
 *
 * After a fresh signup → intake-complete → logout/login (or any path
 * that drops sessionId from the store), the active-session query
 * returns null because PROFILO PRONTO flipped the row to
 * `status=complete`. This query is the second probe — when both
 * activeSession AND latestCompletedSession return null, /recommendation
 * legitimately has nothing to show and renders the no-session state.
 *
 * The two queries share the same response shape so the hydrateSession
 * action treats either source identically.
 */
export function useLatestCompletedSessionQuery(
  enabled: boolean,
): UseQueryResult<ActiveSessionResponse | null> {
  return useQuery({
    queryKey: qk.latestCompletedSession,
    queryFn: () => api.getLatestCompletedSession(),
    enabled,
    staleTime: 0,
    retry: false,
  });
}

export function useResumeSessionMutation(): UseMutationResult<
  ActiveSessionResponse,
  Error,
  string
> {
  const hydrateSession = useSession((s) => s.hydrateSession);
  return useMutation({
    mutationFn: (sessionId: string) => api.getSession(sessionId),
    onSuccess: (data) => hydrateSession(data),
  });
}

export function useSessionsListQuery(): UseQueryResult<SessionSummary[]> {
  const token = useAuth((s) => s.token);
  return useQuery({
    queryKey: qk.sessionsList,
    queryFn: () => api.listSessions(),
    enabled: !!token,
    staleTime: 30_000,
  });
}

export function useDeleteSessionMutation(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId) => api.deleteSession(sessionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.sessionsList });
      void queryClient.invalidateQueries({ queryKey: qk.activeSession });
    },
  });
}

export function useDeleteLastPairMutation(): UseMutationResult<
  DeleteLastPairResponse,
  Error,
  string
> {
  return useMutation({
    mutationFn: (sessionId) => api.deleteLastMessagePair(sessionId),
  });
}

export function useProfileSummaryQuery(): UseQueryResult<UserProfileSummaryResponse> {
  const token = useAuth((s) => s.token);
  return useQuery({
    queryKey: qk.profileSummary,
    queryFn: () => api.getMyProfileSummary(),
    enabled: !!token,
    staleTime: 30_000,
  });
}

// ─── /sessions/{id}/messages ─────────────────────────────────

export function useSendMessageMutation(): UseMutationResult<
  ChatResponse,
  Error,
  { sessionId: string; body: ChatRequest }
> {
  const appendUser = useSession((s) => s.appendUser);
  const appendAssistant = useSession((s) => s.appendAssistant);

  return useMutation({
    mutationFn: ({ sessionId, body }) => api.sendMessage(sessionId, body),
    onMutate: ({ body }) => {
      // Optimistic: drop the user's message into the thread immediately
      // so the UI doesn't have a void between send and reply.
      appendUser(body.message);
    },
    onSuccess: (response) => {
      appendAssistant(response);
    },
  });
}

// ─── /sessions/{id}/recommend ────────────────────────────────

export function useRecommendQuery(
  sessionId: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<RecommendResponse> {
  return useQuery({
    queryKey: qk.recommend(sessionId ?? "none"),
    queryFn: () => {
      if (!sessionId) throw new Error("No active session");
      return api.recommend(sessionId, {});
    },
    enabled: Boolean(sessionId) && options?.enabled !== false,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Fetch the persisted recommendation for a (typically completed) session.
 * 404 if the session has no recommendation yet — caller should fall back
 * to useRecommendMutation/Query to generate one.
 */
export function useGetRecommendationQuery(
  sessionId: string | null,
): UseQueryResult<RecommendResponse> {
  return useQuery({
    queryKey: ["sessions", sessionId ?? "none", "recommendation"],
    queryFn: () => {
      if (!sessionId) throw new Error("No session id");
      return api.getRecommendation(sessionId);
    },
    enabled: Boolean(sessionId),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * ICSP-31 BELT-AND-BRACES: explicit retry policy for the recommend
 * mutation. Never retries on 4xx (the server's verdict is terminal —
 * a 409 intake_not_complete / clinical_change_requires_consult or a
 * 401 auth error will not flip to 200 by retrying). 5xx gets up to two
 * retries with exponential backoff for genuine transient errors.
 *
 * Note: this is NOT the load-bearing fix for the QA Run 3 retry storm.
 * The primary fix is the useEffect loop guard in Recommendation.tsx —
 * the pre-hotfix effect was re-firing `mutate` on every render once
 * `generate.isError` was true, which no `retry` policy can stop. This
 * policy is defense-in-depth: documents intent + locks against a
 * future global default change.
 *
 * Exported so a test (TS-level today; vitest if/when infra lands) can
 * pin the policy without importing React Query internals. Per the
 * wave-1 path-(a) precedent, the function signature itself is the
 * compile-time contract.
 */
export function shouldRetryRecommend(
  failureCount: number,
  error: Error,
): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < 2;
}

function recommendRetryDelayMs(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 8000);
}

export function useRecommendMutation(): UseMutationResult<
  RecommendResponse,
  Error,
  { sessionId: string; body?: RecommendRequest }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, body }) =>
      api.recommend(sessionId, body ?? {}),
    onSuccess: (data, { sessionId }) => {
      queryClient.setQueryData(qk.recommend(sessionId), data);
    },
    retry: shouldRetryRecommend,
    retryDelay: recommendRetryDelayMs,
  });
}

// ─── /catalog/* ──────────────────────────────────────────────

export function useIngredientsQuery(): UseQueryResult<IngredientSummary[]> {
  return useQuery({
    queryKey: qk.catalogIngredients,
    queryFn: () => api.listIngredients(),
    staleTime: 5 * 60_000,
  });
}

export function useSkusQuery(): UseQueryResult<SkuSummary[]> {
  return useQuery({
    queryKey: qk.catalogSkus,
    queryFn: () => api.listSkus(),
    staleTime: 5 * 60_000,
  });
}

// ─── /auth/* ─────────────────────────────────────────────────

export function useSignupMutation(): UseMutationResult<
  SignupResponse,
  Error,
  SignupRequest
> {
  return useMutation({
    mutationFn: (body) => api.signup(body),
  });
}

export function useVerifyEmailMutation(): UseMutationResult<
  GenericOkResponse,
  Error,
  string
> {
  return useMutation({
    mutationFn: (token) => api.verifyEmail(token),
  });
}

/**
 * Verify-email is a one-shot, side-effecting endpoint, but we model it as a
 * query keyed by token so React Query dedupes across StrictMode's mount-
 * unmount-remount in dev (a useEffect+useRef pattern reset the ref on remount
 * and double-fired the mutation, breaking the second call with 400 token-used).
 * Cache lives forever (gcTime: Infinity) so the success/error result doesn't
 * disappear if the user navigates away and back.
 */
export function useVerifyEmailQuery(
  token: string | null,
): UseQueryResult<GenericOkResponse> {
  return useQuery({
    queryKey: ["auth", "verify-email", token ?? ""],
    queryFn: () => api.verifyEmail(token!),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useLoginMutation(): UseMutationResult<
  AuthTokenResponse,
  Error,
  LoginRequest
> {
  const loginSuccess = useAuth((s) => s.loginSuccess);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.login(body),
    onSuccess: (data) => {
      loginSuccess({ token: data.access_token, user: data.user });
      queryClient.setQueryData(qk.me, data.user);
    },
  });
}

export function useMeQuery(): UseQueryResult<MeResponse> {
  const token = useAuth((s) => s.token);
  return useQuery({
    queryKey: qk.me,
    queryFn: () => api.me(),
    enabled: !!token,
    staleTime: 60_000,
  });
}

/**
 * ICSP-43: pre-chat adult self-certification for legacy accounts (those
 * whose `adult_declared` is still false). On success we write the updated
 * /me into the cache so the gate flips closed → open on the same render.
 */
export function useDeclareAdultMutation(): UseMutationResult<
  MeResponse,
  Error,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.declareAdult(),
    onSuccess: (data) => {
      queryClient.setQueryData(qk.me, data);
    },
  });
}

export function usePasswordResetRequestMutation(): UseMutationResult<
  GenericOkResponse,
  Error,
  PasswordResetRequest
> {
  return useMutation({
    mutationFn: (body) => api.passwordResetRequest(body),
  });
}

export function usePasswordResetConfirmMutation(): UseMutationResult<
  GenericOkResponse,
  Error,
  PasswordResetConfirm
> {
  return useMutation({
    mutationFn: (body) => api.passwordResetConfirm(body),
  });
}
